<template>
  <div class="min-h-screen flex items-center justify-center bg-muted/30 py-12 px-4">
    <div class="w-full max-w-sm space-y-6">

      <!-- Header -->
      <div class="text-center space-y-1.5">
        <h1 class="text-2xl font-semibold tracking-tight">Welcome back</h1>
        <p class="text-sm text-muted-foreground">Sign in to your Applypa account</p>
      </div>

      <!-- Card -->
      <div class="rounded-xl border border-border bg-card shadow-sm">
        <div class="p-6 space-y-4">
          <form @submit.prevent="handleLogin" class="space-y-4">

            <!-- Email -->
            <div class="space-y-1.5">
              <label for="email" class="text-sm font-medium leading-none">Email address</label>
              <input
                v-model="email"
                id="email"
                type="email"
                class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                placeholder="you@example.com"
                required
                autocomplete="email"
              />
              <p v-if="errors.email" class="text-xs text-destructive">{{ errors.email }}</p>
            </div>

            <!-- Password -->
            <div class="space-y-1.5">
              <div class="flex items-center justify-between">
                <label for="password" class="text-sm font-medium leading-none">Password</label>
                <router-link to="/forgot-password" class="text-xs text-primary hover:underline">
                  Forgot password?
                </router-link>
              </div>
              <div class="relative">
                <input
                  v-model="password"
                  id="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors pr-10"
                  placeholder="Enter your password"
                  required
                  autocomplete="current-password"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  tabindex="-1"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" class="text-xs"></i>
                </button>
              </div>
              <p v-if="errors.password" class="text-xs text-destructive">{{ errors.password }}</p>
            </div>

            <!-- Remember me -->
            <div class="flex items-center gap-2">
              <input
                v-model="rememberMe"
                id="remember-me"
                type="checkbox"
                class="h-4 w-4 rounded border-input text-primary focus:ring-primary"
              />
              <label for="remember-me" class="text-sm text-muted-foreground">Remember me</label>
            </div>

            <!-- Error -->
            <div v-if="loginError" class="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive flex items-start gap-2">
              <i class="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
              <span>{{ loginError }}</span>
            </div>

            <!-- Submit -->
            <button
              type="submit"
              :disabled="loading"
              class="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin text-xs"></i>
              {{ loading ? 'Signing in...' : 'Sign in' }}
            </button>
          </form>

          <!-- Divider -->
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-border"></div>
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <!-- In-app browser warning (Facebook/Instagram WebView) -->
          <div v-if="isInAppBrowser" class="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2">
            <i class="fas fa-exclamation-triangle mt-0.5 shrink-0 text-amber-500"></i>
            <div>
              <p class="font-medium">Google sign-in is not supported here</p>
              <p class="mt-0.5 text-xs text-amber-700">You're using Facebook's browser. Please open this page in <strong>Chrome or Safari</strong> to sign in with Google.</p>
              <button
                type="button"
                @click="copyPageUrl"
                class="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
              >
                <i class="fas fa-copy text-xs"></i>
                {{ urlCopied ? 'Link copied!' : 'Copy link to open in Chrome' }}
              </button>
            </div>
          </div>

          <!-- Google -->
          <button
            type="button"
            @click="handleGoogleLogin"
            :disabled="googleLoading || isInAppBrowser"
            class="inline-flex w-full items-center justify-center gap-2.5 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <i v-if="googleLoading" class="fas fa-spinner fa-spin text-xs"></i>
            <i v-else class="fab fa-google text-red-500 text-sm"></i>
            {{ googleLoading ? 'Connecting...' : 'Continue with Google' }}
          </button>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-border bg-muted/20 rounded-b-xl">
          <p class="text-center text-sm text-muted-foreground">
            Don't have an account?
            <router-link to="/register" class="font-medium text-primary hover:underline">Sign up</router-link>
          </p>
        </div>
      </div>

      <!-- Terms -->
      <p class="text-center text-xs text-muted-foreground">
        By signing in, you agree to our
        <a href="#" class="text-primary hover:underline">Terms</a>
        and
        <a href="#" class="text-primary hover:underline">Privacy Policy</a>.
      </p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import apiClient from '@/services/api';
import authStore from '@/store/auth'; // Import the auth store

export default {
  name: 'LoginPage',
  setup() {
    const router = useRouter();
    const route = useRoute();

    // Form fields
    const email = ref('');
    const password = ref('');
    const rememberMe = ref(false);
    const showPassword = ref(false);

    // Detect Facebook/Instagram in-app browser (blocks Google OAuth)
    const isInAppBrowser = /FBAN|FBAV|FB_IAB|Instagram|fban|fbav/i.test(navigator.userAgent || '');
    const urlCopied = ref(false);
    const copyPageUrl = () => {
      navigator.clipboard.writeText(window.location.href).then(() => {
        urlCopied.value = true;
        setTimeout(() => { urlCopied.value = false; }, 3000);
      });
    };

    // Form state
    const loading = ref(false);
    const googleLoading = ref(false);
    const loginError = ref('');
    const errors = ref({
      email: '',
      password: ''
    });

    // Google sign-in handler
    const handleGoogleLogin = async () => {
      googleLoading.value = true;

      try {
        const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;

        // Use state parameter to ensure we can identify the redirect back
        const state = 'login-' + Math.random().toString(36).substring(2, 15);
        localStorage.setItem('oauth_state', state);
        if (route.query.redirect) {
          localStorage.setItem('oauth_redirect', route.query.redirect);
        }

        // Construct the Google OAuth URL
        const oauthUrl = `https://accounts.google.com/o/oauth2/auth?` +
          `client_id=${clientId}` +
          `&redirect_uri=${encodeURIComponent(window.location.origin + '/api/auth/google/callback/')}` +
          `&response_type=code` +
          `&scope=${encodeURIComponent('profile email')}` +
          `&state=${state}` +
          `&prompt=select_account`;

        // Redirect to the Google OAuth URL
        window.location.href = oauthUrl;
      } catch (error) {
        googleLoading.value = false;
        loginError.value = 'Failed to connect to Google. Please try again.';
      }
    };

    // Handle login submission
    const handleLogin = async () => {
      // Reset errors
      loginError.value = '';
      errors.value = {
        email: '',
        password: ''
      };

      // Basic validation
      let isValid = true;

      if (!email.value.trim()) {
        errors.value.email = 'Email address is required';
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(email.value)) {
        errors.value.email = 'Please enter a valid email address';
        isValid = false;
      }

      if (!password.value) {
        errors.value.password = 'Password is required';
        isValid = false;
      } else if (password.value.length < 6) {
        errors.value.password = 'Password must be at least 6 characters';
        isValid = false;
      }

      if (!isValid) return;

      // Submit form
      loading.value = true;

      try {
        const response = await apiClient.post('/auth/login/', {
          email: email.value,
          password: password.value,
          remember_me: rememberMe.value
        });

        // Store token in localStorage or in a secure way
        const token = response.data.token;
        localStorage.setItem('token', token);

        // Set token in API client for future requests
        apiClient.defaults.headers.common['Authorization'] = `Token ${token}`;

        // Update the auth store
        authStore.setAuth(true, response.data.user); // Update the store with user data

        // Fetch the latest user data to ensure onboarding status is up-to-date
        try {
          const userResponse = await apiClient.get('/auth/user/');
          // Update auth store with the latest user data
          authStore.setAuth(true, userResponse.data);
          const userData = userResponse.data;

          // Use user data from the latest response to determine onboarding status
          // Redirect back to the page the user came from, or send jobseekers who never
          // finished the onboarding wizard back to it instead of straight to /dashboard
          localStorage.setItem('showOnboardingModal', 'true');
          const redirectTo = route.query.redirect ||
            (userData && userData.account_type === 'jobseeker' && !userData.onboarding_completed
              ? '/onboarding'
              : '/dashboard');
          router.push(redirectTo);
        } catch (userError) {
          // Fall back to the data from the original login response
          const redirectTo = route.query.redirect || response.data.redirect_url || '/dashboard';
          router.push(redirectTo);
        }
      } catch (error) {
        if (error.response) {
          // Handle different error statuses
          switch (error.response.status) {
            case 400: {
              // Bad request - validation errors
              const responseData = error.response.data;
              if (responseData.email) {
                errors.value.email = responseData.email[0];
              }
              if (responseData.password) {
                errors.value.password = responseData.password[0];
              }
              if (responseData.non_field_errors) {
                loginError.value = responseData.non_field_errors[0];
              }
              break;
            }
            case 401: {
              // Unauthorized - invalid credentials
              loginError.value = 'Invalid email or password';
              break;
            }
            case 429: {
              // Too many requests
              loginError.value = 'Too many login attempts. Please try again later.';
              break;
            }
            default: {
              loginError.value = 'An error occurred. Please try again.';
              break;
            }
          }
        } else {
          loginError.value = 'Network error. Please check your connection and try again.';
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      email,
      password,
      rememberMe,
      showPassword,
      loading,
      googleLoading,
      loginError,
      errors,
      handleLogin,
      handleGoogleLogin,
      isInAppBrowser,
      urlCopied,
      copyPageUrl,
    };
  }
};
</script>
